import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users as UsersIcon, ChevronRight, UserPlus, Pencil } from "lucide-react";

// Iter 73 — Ensemble/character groups editor. Sits under the Characters
// section in Company Setup. Supports one level of nesting (parent + kids)
// which matches the "All Ensemble → Female Ensemble" pattern the director
// asked for. Each group can hold direct users AND/OR existing characters.
//
// Iter 74 fix: rewritten to a flat two-tier render (top group + inline
// children loop) instead of a self-recursive GroupCard — the earlier
// recursion tripped Babel's traversal limit and blocked webpack compile.
const NONE = "__none__";  // Select can't use empty string as a value

export default function CharacterGroupsSection({ productionId, castUsers }) {
  const [groups, setGroups] = useState([]);
  const [chars, setChars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", parent_group_id: NONE });
  const [open, setOpen] = useState(false);
  const [addingTo, setAddingTo] = useState(null);
  const [addForm, setAddForm] = useState({ user_ids: [], character_ids: [] });

  const load = async () => {
    try {
      const [g, c] = await Promise.all([
        api.get(`/character-groups`),
        api.get(`/characters`),
      ]);
      setGroups(g.data || []);
      setChars(c.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load groups"); }
  };
  useEffect(() => { if (productionId) load(); /* eslint-disable-next-line */ }, [productionId]);

  // Build a two-tier list: [{...top, children: [child1, child2]}, ...]
  const tree = useMemo(() => {
    const tops = groups.filter((g) => !g.parent_group_id);
    return tops.map((t) => ({
      ...t,
      children: groups.filter((g) => g.parent_group_id === t.id),
    }));
  }, [groups]);

  const parentOptions = useMemo(
    () => groups.filter((g) => !g.parent_group_id && g.id !== editing?.id),
    [groups, editing]
  );

  const userMap = useMemo(() => {
    const m = {};
    castUsers.forEach((u) => { m[u.id] = u; });
    return m;
  }, [castUsers]);
  const charMap = useMemo(() => {
    const m = {};
    chars.forEach((c) => { m[c.id] = c; });
    return m;
  }, [chars]);
  const ungroupedChars = chars.filter((c) => !c.group_id);
  const ungroupedCast = castUsers.filter((u) =>
    !groups.some((g) => (g.direct_user_ids || []).includes(u.id))
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", parent_group_id: NONE });
    setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, parent_group_id: g.parent_group_id || NONE });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      name: form.name.trim(),
      parent_group_id: form.parent_group_id === NONE ? null : form.parent_group_id,
      is_ensemble: true,
    };
    try {
      if (editing) await api.patch(`/character-groups/${editing.id}`, payload);
      else await api.post(`/character-groups`, payload);
      toast.success(editing ? "Group updated" : "Group created");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (g) => {
    if (!confirm(`Delete "${g.name}"?\n\nMembers stay in place — they just lose this grouping. Sub-groups become top-level.`)) return;
    try {
      await api.delete(`/character-groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const openAdd = (g) => {
    setAddingTo(g);
    setAddForm({ user_ids: [], character_ids: [] });
  };
  const submitAdd = async () => {
    if (addForm.user_ids.length === 0 && addForm.character_ids.length === 0) {
      return toast.error("Pick at least one person or character");
    }
    try {
      const { data } = await api.post(`/character-groups/${addingTo.id}/members`, addForm);
      toast.success(`Added ${data.added_users} people + ${data.added_characters} characters`);
      setAddingTo(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const removeMember = async (g, userId) => {
    if (!confirm("Remove from this group?")) return;
    try { await api.delete(`/character-groups/${g.id}/members/${userId}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  const removeCharacter = async (g, characterId) => {
    if (!confirm("Remove this character from the group?")) return;
    try { await api.delete(`/character-groups/${g.id}/characters/${characterId}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div data-testid="setup-groups" className="rounded-lg border border-white/10 bg-[#121212] p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-gold" />
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Ensemble & Groups</div>
          </div>
          <div className="text-xs text-zinc-400 mt-1">Bundle actors and characters so you can call them together. One parent, one level of nesting.</div>
        </div>
        <Button data-testid="setup-groups-add-btn" onClick={openCreate} className="h-8 bg-gold text-black hover:bg-gold-hover">
          <Plus className="w-3.5 h-3.5 mr-1" /> New group
        </Button>
      </div>

      {tree.length === 0 && (
        <div className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-md p-6 text-center">
          No groups yet. Create "All Ensemble" and add sub-groups underneath.
        </div>
      )}

      <div className="space-y-3">
        {tree.map((t) => {
          const topUsers = (t.direct_user_ids || []).map((id) => userMap[id]).filter(Boolean);
          const topChars = (t.character_ids || []).map((id) => charMap[id]).filter(Boolean);
          return (
            <div key={t.id} className="space-y-2">
              <div data-testid={`group-card-${t.id}`} className="rounded-md border border-white/10 bg-[#0A0A0A]">
                <div className="p-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-zinc-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{t.name}</div>
                    <div className="text-[11px] text-zinc-500">
                      {t.total_user_count} member{t.total_user_count === 1 ? "" : "s"}
                      {(t.children?.length || 0) > 0 ? ` · ${t.children.length} sub-group${t.children.length === 1 ? "" : "s"}` : ""}
                    </div>
                  </div>
                  <button data-testid={`group-add-${t.id}`} onClick={() => openAdd(t)} title="Add members" className="text-zinc-400 hover:text-gold p-1"><UserPlus className="w-4 h-4" /></button>
                  <button data-testid={`group-edit-${t.id}`} onClick={() => openEdit(t)} title="Edit" className="text-zinc-400 hover:text-gold p-1"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`group-del-${t.id}`} onClick={() => del(t)} title="Delete" className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                {(topUsers.length > 0 || topChars.length > 0) ? (
                  <div className="border-t border-white/5 p-3 flex flex-wrap gap-1.5">
                    {topUsers.map((u) => (
                      <span key={u.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                        {u.name}<button onClick={() => removeMember(t, u.id)} className="text-zinc-500 hover:text-red-400 ml-0.5">×</button>
                      </span>
                    ))}
                    {topChars.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gold/10 border border-gold/25 text-gold">
                        {c.name}<button onClick={() => removeCharacter(t, c.id)} className="text-zinc-500 hover:text-red-400 ml-0.5">×</button>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              {(t.children || []).map((child) => {
                const cu = (child.direct_user_ids || []).map((id) => userMap[id]).filter(Boolean);
                const cc = (child.character_ids || []).map((id) => charMap[id]).filter(Boolean);
                return (
                  <div key={child.id} data-testid={`group-card-${child.id}`} className="rounded-md border border-white/10 bg-[#0A0A0A] ml-6">
                    <div className="p-3 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4 text-zinc-600 rotate-90" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{child.name}</div>
                        <div className="text-[11px] text-zinc-500">{child.total_user_count} member{child.total_user_count === 1 ? "" : "s"}</div>
                      </div>
                      <button data-testid={`group-add-${child.id}`} onClick={() => openAdd(child)} title="Add members" className="text-zinc-400 hover:text-gold p-1"><UserPlus className="w-4 h-4" /></button>
                      <button data-testid={`group-edit-${child.id}`} onClick={() => openEdit(child)} title="Edit" className="text-zinc-400 hover:text-gold p-1"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`group-del-${child.id}`} onClick={() => del(child)} title="Delete" className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {(cu.length > 0 || cc.length > 0) ? (
                      <div className="border-t border-white/5 p-3 flex flex-wrap gap-1.5">
                        {cu.map((u) => (
                          <span key={u.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                            {u.name}<button onClick={() => removeMember(child, u.id)} className="text-zinc-500 hover:text-red-400 ml-0.5">×</button>
                          </span>
                        ))}
                        {cc.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gold/10 border border-gold/25 text-gold">
                            {c.name}<button onClick={() => removeCharacter(child, c.id)} className="text-zinc-500 hover:text-red-400 ml-0.5">×</button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit group" : "New group"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="setup-groups-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="All Ensemble, Female Ensemble, Kids Chorus…" className="bg-[#0A0A0A] border-white/10" />
            </div>
            <div>
              <Label>Parent group (optional)</Label>
              <Select value={form.parent_group_id} onValueChange={(v) => setForm({ ...form, parent_group_id: v })}>
                <SelectTrigger data-testid="setup-groups-parent-select" className="bg-[#0A0A0A] border-white/10 mt-1">
                  <SelectValue placeholder="Top-level" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 text-zinc-200">
                  <SelectItem value={NONE}>Top-level (no parent)</SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 mt-1">Nesting is capped at one level. When you call the parent, everyone in every child group is called too.</div>
            </div>
          </div>
          <DialogFooter><Button data-testid="setup-groups-save-btn" onClick={save} className="bg-gold text-black hover:bg-gold-hover">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add members dialog */}
      {addingTo && (
        <Dialog open={!!addingTo} onOpenChange={(v) => !v && setAddingTo(null)}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">Add to {addingTo.name}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>People (cast members)</Label>
                <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-[#0A0A0A] divide-y divide-white/5">
                  {ungroupedCast.length === 0 && <div className="p-3 text-xs text-zinc-500">Everyone is grouped already.</div>}
                  {ungroupedCast.map((u) => {
                    const active = addForm.user_ids.includes(u.id);
                    return (
                      <label key={u.id} data-testid={`group-add-user-${u.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/5 ${active ? "bg-gold/10 text-gold" : "text-zinc-200"}`}>
                        <input type="checkbox" checked={active} onChange={() =>
                          setAddForm((f) => ({ ...f, user_ids: active ? f.user_ids.filter((x) => x !== u.id) : [...f.user_ids, u.id] }))
                        } />
                        <span className="truncate">{u.name}</span>
                        {u.character && <span className="text-[10px] text-zinc-500 ml-auto truncate">{u.character}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Characters (roll-up bit-parts)</Label>
                <div className="mt-2 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-[#0A0A0A] divide-y divide-white/5">
                  {ungroupedChars.length === 0 && <div className="p-3 text-xs text-zinc-500">All characters already belong to a group.</div>}
                  {ungroupedChars.map((c) => {
                    const active = addForm.character_ids.includes(c.id);
                    return (
                      <label key={c.id} data-testid={`group-add-char-${c.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/5 ${active ? "bg-gold/10 text-gold" : "text-zinc-200"}`}>
                        <input type="checkbox" checked={active} onChange={() =>
                          setAddForm((f) => ({ ...f, character_ids: active ? f.character_ids.filter((x) => x !== c.id) : [...f.character_ids, c.id] }))
                        } />
                        <span className="truncate">{c.name}</span>
                        {c.actor_user_id && <span className="text-[10px] text-zinc-500 ml-auto truncate">{userMap[c.actor_user_id]?.name}</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="setup-groups-add-members-btn" onClick={submitAdd} className="bg-gold text-black hover:bg-gold-hover">Add {addForm.user_ids.length + addForm.character_ids.length}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
